import ApplicationComponent from "legacy/ApplicationComponent"
import {
  centsToDollars,
  dollarsToCents,
  humanize,
  isBlank,
  isPresent,
  safePercentageDecimal,
} from "legacy/helpers.es6"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"
import { v4 as uuidv4 } from "uuid"

class FeesAndDiscountsForm extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = this.blankState()
    this.handleFirstMonthChange = this.handleFirstMonthChange.bind(this)
    this.handleLastMonthChange = this.handleLastMonthChange.bind(this)
    this.handleFeeLengthChange = this.handleFeeLengthChange.bind(this)
    this.validator = validatorFactory({ autoForceUpdate: this })

    // Create refs for input fields
    this.inputRefs = {
      feeName: React.createRef(),
      feeAmount: React.createRef(),
      amount: React.createRef(),
    }
  }

  blankState() {
    return {
      feeCategory: this.props.feeCategories[0],
      feeName: "",
      feeAmount: "",
      feeLength: "full_stay",
      startMonth: undefined,
      endMonth: undefined,
      amountType: "dollars",
    }
  }

  hasUnsavedChanges() {
    return !!(
      this.inputRefs.feeName.current?.value ||
      this.inputRefs.feeAmount.current?.value
    )
  }

  handleFeeLengthChange(event) {
    const feeLength = event.target.value
    let startMonth = null
    let endMonth = null
    if (feeLength === "monthly") {
      startMonth = this.monthsAndYearArray()[0]
      endMonth = this.monthsAndYearArray()[0]
    }
    this.setState({
      feeLength,
      startMonth,
      endMonth,
    })
  }

  handleFirstMonthChange(event) {
    this.handleMonthChange(
      "startMonth",
      event.target.value,
      this.state.endMonth
    )
  }

  handleLastMonthChange(event) {
    this.handleMonthChange(
      "endMonth",
      this.state.startMonth,
      event.target.value
    )
  }

  handleMonthChange(setting, startMonth, endMonth) {
    const monthsArray = this.monthsAndYearArray()
    const startIndex = monthsArray.indexOf(startMonth)
    const endIndex = monthsArray.indexOf(endMonth)
    if (startIndex > endIndex) {
      if (setting === "startMonth") {
        endMonth = monthsArray[startIndex]
      } else {
        startMonth = monthsArray[endIndex]
      }
    }
    this.setState({
      startMonth,
      endMonth,
    })
  }

  isFormUnsaved() {
    return this.state.feeCategory && this.state.feeName && this.state.feeAmount
  }

  hasUnsavedFeeOrDiscount() {
    if (this.isFormUnsaved()) {
      return !confirm(
        "You have an unsaved Fee, Electric, or Discount, would you like to continue without adding it to the pricing?"
      )
    }
    return false
  }

  addFee() {
    this.validator.showMessages()
    if (this.validator.allValid()) {
      const feesAndDiscounts = this.props.feesAndDiscounts
      const feeObject = {
        category: this.state.feeCategory,
        name: this.state.feeName,
      }
      if (this.state.startMonth && this.state.endMonth) {
        feeObject.start_date = moment(this.state.startMonth, "MMMM YYYY")
          .startOf("month")
          .format("YYYY-MM-DD")
        feeObject.end_date = moment(this.state.endMonth, "MMMM YYYY")
          .endOf("month")
          .format("YYYY-MM-DD")
      }
      if (this.state.amountType === "percentage") {
        feeObject.percentage = this.state.feeAmount / 100
      } else {
        feeObject.amount = dollarsToCents(this.state.feeAmount)
      }
      feesAndDiscounts.push(feeObject)
      this.props.updateFeesAndDiscounts(feesAndDiscounts)
      this.validator.hideMessages()
      this.setState(this.blankState())
    }
  }

  removeFee(fee) {
    this.props.updateFeesAndDiscounts(
      _.reject(this.props.feesAndDiscounts, (current) => current === fee)
    )
  }

  feesTable() {
    if (this.props.feesAndDiscounts.length) {
      return (
        <div className="table-responsive bs-mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Name</th>
                <th>Amount</th>
                <th>Pricing Structure</th>
              </tr>
            </thead>
            <tbody>{this.feesTableBody()}</tbody>
          </table>
        </div>
      )
    }
    return null
  }

  feesTableBody() {
    return _.map(this.props.feesAndDiscounts, (fee) => {
      return (
        <tr key={`${fee.name}-${uuidv4()}`}>
          <td>{humanize(fee.category)}</td>
          <td>{fee.name}</td>
          <td>
            {isPresent(fee.percentage)
              ? `${safePercentageDecimal(fee.percentage)}%`
              : centsToDollars(fee.amount)}
          </td>
          {fee.start_date && fee.end_date ? (
            <td>
              {moment(fee.start_date).format("MMMM YYYY")} -{" "}
              {moment(fee.end_date).format("MMMM YYYY")}
            </td>
          ) : (
            <td>{this.isMonthlyPricing() ? "Monthly" : "Full Stay"} </td>
          )}
          <td
            role="button"
            className="text-danger"
            onClick={() => this.removeFee(fee)}
            tabIndex="0"
          >
            Remove
          </td>
        </tr>
      )
    })
  }

  isMonthlyPricing() {
    return this.props.pricingStructure.includes("_month")
  }

  isDiscount() {
    return this.state.feeCategory === "discount"
  }

  lengthOfFee() {
    if (this.props.hideLengthOfFeeOption || !this.isMonthlyPricing())
      return null

    return (
      <div className="col-sm-12">
        <div className="form-group">
          <label htmlFor="feeLength">{`Length of ${humanize(
            this.state.feeCategory
          )}`}</label>
          <select
            id="feeLength"
            className="form-control"
            name="feeLength"
            value={this.state.feeLength}
            onChange={this.handleFeeLengthChange}
          >
            <option value="full_stay">Full stay</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>
    )
  }

  monthsAndYearArray() {
    if (isBlank(this.props.startDate) || isBlank(this.props.endDate)) return []

    const start = moment(this.props.startDate)
    const end = moment(this.props.endDate)
    const interim = start.clone()
    const months = []
    // eslint-disable-next-line no-unmodified-loop-condition
    while (end > interim || interim.format("M") === end.format("M")) {
      months.push(interim.format("MMMM YYYY"))
      interim.add(1, "month")
    }
    return months
  }

  monthlyFeeLength() {
    if (!this.isMonthlyPricing() || this.state.feeLength !== "monthly")
      return null

    if (isBlank(this.props.startDate) || isBlank(this.props.endDate)) {
      return (
        <div className="text-danger bs-px-3 bs-pb-3">
          You must add Arrival and Departure dates above before you can select
          months here.
        </div>
      )
    }

    return [
      <div className="col-sm-4" key="0">
        <div className="form-group">
          <label htmlFor="startMonth">First month</label>
          <select
            id="startMonth"
            className="form-control"
            name="startMonth"
            value={this.state.startMonth}
            onChange={this.handleFirstMonthChange}
          >
            {_.map(this.monthsAndYearArray(), (month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        </div>
      </div>,
      <div className="col-sm-4" key="1">
        <div className="form-group">
          <label htmlFor="endMonth">Last month</label>
          <select
            id="endMonth"
            className="form-control"
            name="endMonth"
            value={this.state.endMonth}
            onChange={this.handleLastMonthChange}
          >
            {_.map(this.monthsAndYearArray(), (month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        </div>
      </div>,
    ]
  }

  render() {
    return (
      <div className="row">
        <div
          className={
            this.props.hideLeftColClass || this.props.colWidth || "col-sm-3"
          }
        >
          <h4 className="mt-0">Fees, Electric & Discounts</h4>
        </div>
        <div className={this.props.colWidth || "col-sm-9"}>
          <div className="row form-row">
            <div className="col-sm-3">
              <div className="form-group">
                <label htmlFor="feeCategory">Category</label>
                <select
                  id="feeCategory"
                  className="form-control pr-0"
                  name="feeCategory"
                  value={this.state.feeCategory}
                  onChange={this.handleInputChange}
                >
                  {_.map(this.props.feeCategories, (feeCategory) => (
                    <option key={feeCategory} value={feeCategory}>
                      {humanize(feeCategory)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="form-group">
                <label htmlFor="feeName">Name</label>
                <input
                  id="feeName"
                  ref={this.inputRefs.feeName}
                  className="form-control"
                  type="text"
                  name="feeName"
                  required="required"
                  value={this.state.feeName}
                  onChange={this.handleInputChange}
                />
                {this.validator.message("name", this.state.feeName, "required")}
              </div>
            </div>
            <div className="col-sm-5">
              <div className="form-group">
                <label htmlFor="amountType">{`${
                  this.isDiscount() ? "Discount" : "Price"
                } per ${
                  this.isMonthlyPricing() ? "Month" : "Full Stay"
                }`}</label>
                <div className="row form-row">
                  <div className="col-sm-4">
                    <select
                      id="amountType"
                      className="form-control pr-0"
                      name="amountType"
                      value={this.state.amountType}
                      onChange={this.handleInputChange}
                    >
                      <option value="dollars">$</option>
                      <option value="percentage">%</option>
                    </select>
                  </div>
                  <div className="col-sm-8">
                    <input
                      ref={this.inputRefs.feeAmount}
                      className="form-control"
                      type="text"
                      name="feeAmount"
                      required="required"
                      value={this.state.feeAmount}
                      onChange={this.handleInputChange}
                    />
                    {this.validator.message(
                      "amount",
                      this.state.feeAmount,
                      `required|numeric|${
                        this.state.amountType === "dollars"
                          ? "min:0"
                          : "between:0,100"
                      },num`
                    )}
                  </div>
                </div>
              </div>
            </div>
            {this.lengthOfFee()}
            {this.monthlyFeeLength()}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => this.addFee()}
          >
            Apply {humanize(this.state.feeCategory)}
          </button>
          {this.feesTable()}
        </div>
      </div>
    )
  }
}

FeesAndDiscountsForm.propTypes = {
  feeCategories: PropTypes.array.isRequired,
  feesAndDiscounts: PropTypes.array.isRequired,
  updateFeesAndDiscounts: PropTypes.func.isRequired,
  pricingStructure: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  colWidth: PropTypes.string,
  hideLengthOfFeeOption: PropTypes.bool,
  hideLeftColClass: PropTypes.string,
  setRef: PropTypes.func,
}

export default FeesAndDiscountsForm
